// Mirror-scene view systems (docs/ARCHITECTURE.md §8 EntityViewSystem / LockLineSystem): each frame, after
// InterpolationSystem resolved the render poses, they write the in-view units, buildings and missiles into the
// GlyphLayer and every weapon lock into the LockLineLayer. The war screen brackets the frame (begin/end).

import { Matcher, type Entity } from '@esengine/ecs-framework'
import {
  Building,
  Health,
  InvariantViolation,
  Owner,
  SimIdentity,
  StrikeFlight,
  TransportHull,
  UnitKind,
  Weapon,
  NO_SIM_ID,
  type MatchSetupView,
  type OwnerValue,
  type SimId,
} from '@taxwar/net'
import type { LatLonOut, MatchView } from '../../match/contracts'
import { PoseTrack, PresentationOrder, PresentationSystem } from '../../match/presentation'
import { isEmbarkedCargo } from '../../match/cargo'
import { BUILDING_RING_SLOTS, type GlyphLayer } from './GlyphLayer'
import type { LockLineLayer } from './LockLineLayer'

/** Golden-ratio spread of per-entity flicker phases, so neighbouring glyphs never pulse together. */
const PHASE_STEP = 0.618033988749895

function phaseOf(entity: Entity): number {
  return (simIdOf(entity) * PHASE_STEP) % 1
}

function ownerOf(entity: Entity): OwnerValue {
  return (entity.getComponent(Owner) as Owner).player as OwnerValue
}

function poseOf(entity: Entity): PoseTrack {
  return entity.getComponent(PoseTrack) as PoseTrack
}

function simIdOf(entity: Entity): number {
  return (entity.getComponent(SimIdentity) as SimIdentity).simId
}

/**
 * A building's slot on its city's ring: its rank by SimId among the buildings in view in the same city, so two
 * buildings of one city never share a slot and a slot does not depend on the query's entity order. Each frame files
 * every building under its city, then ranks it within that city's short list: O(buildings × slots), never
 * buildings², and allocation-free (a World War can show thousands of buildings).
 */
class RingSlots {
  /** City SimId → CityIndex (static for the war). */
  private readonly cityIndexOf: ReadonlyMap<number, number>
  /** Per city: buildings filed this frame. */
  private readonly filled: Uint8Array
  /** Per city × ring slot: the SimIds filed this frame. */
  private readonly filedIds: Float64Array
  /** Cities filed into this frame, so the next frame resets only those. */
  private readonly touched: Int32Array
  private touchedCount = 0

  constructor(setup: MatchSetupView) {
    this.cityIndexOf = new Map(setup.cities.map((city) => [city.simId as number, city.index as number]))
    this.filled = new Uint8Array(setup.cities.length)
    this.filedIds = new Float64Array(setup.cities.length * BUILDING_RING_SLOTS)
    this.touched = new Int32Array(setup.cities.length)
  }

  begin(): void {
    for (let i = 0; i < this.touchedCount; i++) this.filled[this.touched[i] as number] = 0
    this.touchedCount = 0
  }

  file(citySimId: number, simId: number): void {
    const city = this.cityOf(citySimId)
    const filled = this.filled[city] as number
    if (filled >= BUILDING_RING_SLOTS) throw new InvariantViolation(`city SimId ${citySimId} shows more than its ${BUILDING_RING_SLOTS} buildings`)
    if (filled === 0) this.touched[this.touchedCount++] = city
    this.filedIds[city * BUILDING_RING_SLOTS + filled] = simId
    this.filled[city] = filled + 1
  }

  rankOf(citySimId: number, simId: number): number {
    const city = this.cityOf(citySimId)
    const base = city * BUILDING_RING_SLOTS
    const filled = this.filled[city] as number
    let rank = 0
    for (let i = 0; i < filled; i++) if ((this.filedIds[base + i] as number) < simId) rank++
    return rank
  }

  cityOf(citySimId: number): number {
    const city = this.cityIndexOf.get(citySimId)
    if (city === undefined) throw new InvariantViolation(`building in unknown city SimId ${citySimId}`)
    return city
  }
}

export class UnitViewSystem extends PresentationSystem {
  constructor(private readonly glyphs: GlyphLayer) {
    super(Matcher.all(UnitKind, Health, Owner, SimIdentity, PoseTrack), PresentationOrder.entityViews)
  }

  protected override present(entities: readonly Entity[]): void {
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i] as Entity
      const kind = entity.getComponent(UnitKind) as UnitKind
      const health = entity.getComponent(Health) as Health
      const pose = poseOf(entity)
      // Embarked ground units are cargo: no ghost ground army at sea, no health bar (the hull shows the cargo pips).
      if (isEmbarkedCargo(entity)) continue
      // A unit spawns with its full synced Health, so max HP is always positive.
      if (!(health.maxHp > 0)) throw new InvariantViolation(`unit SimId ${simIdOf(entity)} has no max HP`)
      this.glyphs.addUnit(kind.typeId, ownerOf(entity), pose.lat, pose.lon, pose.heading, kind.bodies, health.hp / health.maxHp, phaseOf(entity))
    }
  }
}

/** Transport craft: a boat or plane silhouette by mode, with a health bar and cargo-slot pips (taxgw-8lvi, taxgw-rvwq). */
export class TransportViewSystem extends PresentationSystem {
  constructor(private readonly glyphs: GlyphLayer) {
    super(Matcher.all(TransportHull, Health, Owner, SimIdentity, PoseTrack), PresentationOrder.entityViews)
  }

  protected override present(entities: readonly Entity[]): void {
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i] as Entity
      const hull = entity.getComponent(TransportHull) as TransportHull
      const health = entity.getComponent(Health) as Health
      const pose = poseOf(entity)
      if (!(health.maxHp > 0)) throw new InvariantViolation(`transport SimId ${simIdOf(entity)} has no max HP`)
      this.glyphs.addTransport(hull.mode, ownerOf(entity), pose.lat, pose.lon, pose.heading, hull.cargoSlotsUsed, health.hp / health.maxHp, phaseOf(entity))
    }
  }
}

export class BuildingViewSystem extends PresentationSystem {
  private readonly ringSlots: RingSlots

  constructor(
    private readonly glyphs: GlyphLayer,
    setup: MatchSetupView,
  ) {
    super(Matcher.all(Building, Owner, SimIdentity, PoseTrack), PresentationOrder.entityViews)
    this.ringSlots = new RingSlots(setup)
  }

  protected override present(entities: readonly Entity[]): void {
    const slots = this.ringSlots
    slots.begin()
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i] as Entity
      slots.file((entity.getComponent(Building) as Building).citySimId, simIdOf(entity))
    }
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i] as Entity
      const building = entity.getComponent(Building) as Building
      const pose = poseOf(entity)
      const ringSlot = slots.rankOf(building.citySimId, simIdOf(entity))
      this.glyphs.addBuilding(building.typeId, ownerOf(entity), pose.lat, pose.lon, slots.cityOf(building.citySimId), ringSlot, phaseOf(entity))
    }
  }
}

export class StrikeViewSystem extends PresentationSystem {
  constructor(private readonly glyphs: GlyphLayer) {
    super(Matcher.all(StrikeFlight, Owner, SimIdentity, PoseTrack), PresentationOrder.entityViews)
  }

  protected override present(entities: readonly Entity[]): void {
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i] as Entity
      const flight = entity.getComponent(StrikeFlight) as StrikeFlight
      const pose = poseOf(entity)
      this.glyphs.addStrike(flight.typeId, ownerOf(entity), pose.lat, pose.lon, flight.originLat, flight.originLon, flight.targetLat, flight.targetLon, phaseOf(entity))
    }
  }
}

/** A line from every weapon with a lock to its target, when the target is in view. */
export class LockLineSystem extends PresentationSystem {
  private readonly target: LatLonOut = { lat: 0, lon: 0 }

  constructor(
    private readonly match: MatchView,
    private readonly lines: LockLineLayer,
  ) {
    super(Matcher.all(Weapon, Owner, PoseTrack), PresentationOrder.lockLines)
  }

  protected override present(entities: readonly Entity[]): void {
    for (let i = 0; i < entities.length; i++) {
      const entity = entities[i] as Entity
      // Cargo holds no lock and fires nothing; entityPosition also refuses a cargo target (Match.entityPosition).
      if (isEmbarkedCargo(entity)) continue
      const locked = (entity.getComponent(Weapon) as Weapon).lockedTarget as SimId
      if (locked === NO_SIM_ID || !this.match.entityPosition(locked, this.target)) continue
      const pose = poseOf(entity)
      this.lines.add(ownerOf(entity), pose.lat, pose.lon, this.target.lat, this.target.lon)
    }
  }
}
